/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited 2023, 2024.
 */

import { Linkable } from '@/components/blocks/Linkable';
import { MuiCardMedia } from '@/components/blocks/MuiCardMedia';
import { ProgressIndicator } from '@/components/blocks/ProgressIndicator';
import {
	categoryCardLinkSx,
	categoryCardMediaImageBoxSx,
	categoryCardMediaImageSx,
	categoryCardNameSx,
	categoryCardStackNameSx,
	categoryCardStyleContainerSx,
} from '@/components/content/CategoryCard/styles/categoryCardStyles';
import { useCategory } from '@/data/Content/Category';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { useUser } from '@/data/User';
import { COOKIES } from '@/data/constants/cookie';
import { ContentContext } from '@/data/context/content';
import { CookiesSingletonContext } from '@/data/cookie/cookiesSingletonProvider';
import { ID } from '@/data/types/Basic';
import { CategoryType } from '@/data/types/Category';
import { getContractIdFromContext } from '@/utils/getContractIdFromContext';
import { getHref_Category } from '@/utils/getHref_Category';
import { Box, Card, Stack, Typography } from '@mui/material';
import { FC, MouseEvent, useCallback, useContext, useEffect, useMemo } from 'react';
import { NO_IMAGE_PLACE_HOLDER } from 'shared/custom/constants/commonConstants';

export const CategoryCard: FC<{
	id: ID;
	parent?: CategoryType;
	clickAction?: (event?: MouseEvent) => void;
}> = ({ id, clickAction, parent }) => {
	const router = useNextRouter();
	const { getCookie } = useContext(CookiesSingletonContext);
	const trail = useMemo(() => getCookie(COOKIES.breadcrumb), [getCookie]);
	const { rawData, category, loading } = useCategory(id);
	const { user } = useUser();
	const contract = getContractIdFromContext(user?.context);
	const { onNotify } = useContext(ContentContext) as {
		onNotify: (id: ID, contract: string, category?: CategoryType) => void;
	};
	const routeUrl = useMemo(
		() => getHref_Category(category, parent, trail as string[]),
		[category, parent, trail]
	);
	const onClick = useCallback(
		(event: MouseEvent) => {
			if (clickAction) {
				clickAction(event);
			}
			router.push(routeUrl);
		},
		[clickAction, routeUrl, router]
	);

	useEffect(() => {
		if (onNotify && rawData) {
			onNotify(id, contract, category);
		}
	}, [loading, rawData, category]); // eslint-disable-line react-hooks/exhaustive-deps

	return loading ? (
		<ProgressIndicator />
	) : category ? (
		// TODO Hoist href to top level category object, presentation shouldn't have to handle this structure.
		<Card
			onClick={onClick}
			sx={categoryCardStyleContainerSx}
			id={category.name}
			data-testid={category.name}
		>
			<Box sx={categoryCardMediaImageBoxSx}>
				<Linkable
					href={routeUrl}
					color="textPrimary"
					data-testid={category.seo.href || ''}
					sx={categoryCardLinkSx}
					id={category.seo.href || ''}
				>
					<MuiCardMedia
						sx={categoryCardMediaImageSx}
						component="div"
						// image={category.fullImage || category.thumbnail || NO_IMAGE_PLACE_HOLDER}
						image={NO_IMAGE_PLACE_HOLDER}
					></MuiCardMedia>
				</Linkable>
			</Box>

			<Stack sx={categoryCardStackNameSx} spacing={1}>
				<Typography
					variant="bodyBaseline"
					fontWeight="semibold"
					align="center"
					id={`${category.name}-name`}
					data-testid={`${category.name}-name`}
					sx={categoryCardNameSx}
					alpha={true}
				>
					<Linkable sx={{ color: '#ffff' }} href={routeUrl} color="textPrimary">
						{category.name}
					</Linkable>
				</Typography>
			</Stack>
		</Card>
	) : null;
};
